/* ═══════════════════════════════════════════════════════════════
   REVIEW — the reminder on open (SYNC_PLAN D7).

   "3 master updates are waiting for review" · Review now (primary) ·
   Later · Don't show again. Esc / overlay = Later.

   Later = once per session per target (module-level Set, the same
   pattern as Studio's draft notice). Don't show again persists
   `sync.reminder:<targetId>` = { rowId } where rowId is `<rowId>:<toVersion>`
   (additive: an old bare id simply never matches, so it shows once more).
   A new publish (new toVersion) shows the reminder again.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { readReminderDismissal, writeReminderDismissal } from './syncAdapter';
import type { SyncFeedRow } from './types';

/** Targets already reminded this session ("Later"), keyed target + version. */
const remindedThisSession = new Set<string>();

/** The newest pending row — the one a dismissal is pinned to. */
function newestRow(rows: SyncFeedRow[]): SyncFeedRow | null {
  let best: SyncFeedRow | null = null;
  for (const r of rows) if (!best || (r.toVersion ?? 0) > (best.toVersion ?? 0)) best = r;
  return best;
}

/** What a reminder is about: the row AND the version it carries. The server
 *  upserts, so a later publish keeps the row id but bumps `toVersion` —
 *  keyed on the id alone, one "Don't show again" would hide every later one. */
export function reminderIdentity(rows: SyncFeedRow[]): string | null {
  const n = newestRow(rows);
  return n ? `${n.id}:${n.toVersion ?? 0}` : null;
}

/** Pure decision (unit-tested): show, given the identity, the saved
 *  dismissal and the session's already-reminded set. */
export function reminderDue(
  rows: SyncFeedRow[],
  count: number,
  dismissedKey: string | null,
  session: ReadonlySet<string>,
  targetId: string
): boolean {
  if (!rows.length || count <= 0) return false;
  const id = reminderIdentity(rows);
  if (id === null) return false;
  if (session.has(`${targetId}|${id}`)) return false;
  return dismissedKey !== id;
}

/** Should the reminder show for this target's pending rows right now?
 *  Spec updates and project-forma updates are one reminder (D7); identity is
 *  the newest row across BOTH kinds. */
export function shouldRemind(
  targetId: string,
  rows: SyncFeedRow[],
  specCount: number,
  formaCount = 0
): boolean {
  return reminderDue(
    rows,
    specCount + formaCount,
    readReminderDismissal(targetId)?.rowId ?? null,
    remindedThisSession,
    targetId
  );
}

export function markReminded(targetId: string, rows: SyncFeedRow[]): void {
  const id = reminderIdentity(rows);
  if (id) remindedThisSession.add(`${targetId}|${id}`);
}

export function dismissReminder(targetId: string, rows: SyncFeedRow[]): void {
  const id = reminderIdentity(rows);
  if (!id) return;
  remindedThisSession.add(`${targetId}|${id}`);
  writeReminderDismissal(targetId, id);
}

/** "2 master updates and 1 forma update are waiting for review". */
export function reminderTitle(count: number, formaCount = 0): string {
  const spec = count > 0 ? `${count} master ${count === 1 ? 'update' : 'updates'}` : '';
  const forma =
    formaCount > 0 ? `${formaCount} forma ${formaCount === 1 ? 'update' : 'updates'}` : '';
  const both = spec && forma ? `${spec} and ${forma}` : spec || forma;
  const plural = count + formaCount !== 1 || (!!spec && !!forma);
  return `${both} ${plural ? 'are' : 'is'} waiting for review`;
}

interface MasterUpdatesReminderProps {
  open: boolean;
  count: number;
  /** Changes waiting on the project's forma (counted in the same line). */
  formaCount?: number;
  onReviewNow: () => void;
  onLater: () => void;
  onDontShowAgain: () => void;
}

export function MasterUpdatesReminder({
  open,
  count,
  formaCount = 0,
  onReviewNow,
  onLater,
  onDontShowAgain,
}: MasterUpdatesReminderProps) {
  const reviewRef = React.useRef<HTMLButtonElement>(null);
  const title = reminderTitle(count, formaCount);
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onLater();
      }}
    >
      <DialogContent
        hideClose
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          reviewRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Your masters changed since this spec was added. Review the changes and choose which ones
            this project takes.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onDontShowAgain}>
            Don&apos;t show again
          </Button>
          <Button variant="secondary" onClick={onLater}>
            Later
          </Button>
          <Button ref={reviewRef} onClick={onReviewNow}>
            Review now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
